/**
 * ImportModal.jsx
 *
 * Purpose: 3-step modal for importing Accounts or Contacts from Excel/CSV files.
 * Steps: 1) Upload → 2) Preview & Map → 3) Result
 *
 * @param {boolean}  isOpen          — Controls modal visibility
 * @param {function} onClose         — Called when modal is dismissed
 * @param {'accounts'|'contacts'} type — Which entity to import
 * @param {Array}    allAccounts     — Current accounts list (used for contact matching)
 * @param {function} fetchAll        — AppContext fetchAll — called after import to refresh data
 */
import { useState, useRef, useCallback } from 'react';
import { useDraggableDialog } from '../../hooks/useDraggableDialog';
import { acceptDrag, filesOf } from '../../utils/droppedFiles';
import * as XLSX from 'xlsx';
import { Icon } from './Icon';

// ── Styles ──────────────────────────────────────────────────────────────────
const MODAL_OVERLAY = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
  padding: 24,
};
const MODAL_BOX = {
  background: '#fff',
  borderRadius: 10,
  width: '100%',
  maxWidth: 760,
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
};
const HEADER = {
  padding: '18px 24px',
  borderBottom: '1px solid #F0F0F0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
};
const BODY = { overflowY: 'auto', flex: 1, padding: '24px' };
const FOOTER = {
  padding: '16px 24px',
  borderTop: '1px solid #F0F0F0',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
  flexShrink: 0,
};
const BTN_PRIMARY = {
  background: '#1B5E20',
  color: '#fff',
  border: 'none',
  padding: '9px 20px',
  borderRadius: 6,
  fontWeight: 700,
  fontSize: 11,
  cursor: 'pointer',
  fontFamily: 'Montserrat',
};
const BTN_OUTLINE = {
  background: '#fff',
  color: '#1B5E20',
  border: '1px solid #1B5E20',
  padding: '9px 20px',
  borderRadius: 6,
  fontWeight: 700,
  fontSize: 11,
  cursor: 'pointer',
  fontFamily: 'Montserrat',
};
const BTN_CANCEL = {
  background: '#fff',
  color: '#555',
  border: '1px solid #E0E0E0',
  padding: '9px 20px',
  borderRadius: 6,
  fontWeight: 600,
  fontSize: 11,
  cursor: 'pointer',
  fontFamily: 'Montserrat',
};
const DROP_ZONE_BASE = {
  border: '2px dashed #43A047',
  borderRadius: 8,
  padding: '40px 20px',
  textAlign: 'center',
  background: '#F9FFF9',
  cursor: 'pointer',
  transition: 'all 0.15s',
};
const DROP_ZONE_DRAG = {
  ...DROP_ZONE_BASE,
  background: '#E8F5E9',
  borderColor: '#1B5E20',
};
const LABEL = { fontSize: 10.5, fontWeight: 700, color: '#666', marginBottom: 4, display: 'block' };
const SELECT = {
  width: '100%',
  padding: '7px 10px',
  borderRadius: 6,
  border: '1px solid #E0E0E0',
  fontSize: 11,
  fontFamily: 'Montserrat',
  outline: 'none',
  background: '#fff',
};
const TABLE_TH = {
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 700,
  color: '#666',
  background: '#FAFBFC',
  borderBottom: '1px solid #ECECEC',
};
const TABLE_TD = {
  padding: '7px 12px',
  fontSize: 11,
  color: '#333',
  borderBottom: '1px solid #F7F7F7',
  fontFamily: 'Montserrat',
};

// ── Field definitions ────────────────────────────────────────────────────────

const ACCOUNT_FIELDS = [
  { key: 'name', label: 'Company Name', required: true },
  { key: 'industry', label: 'Industry', required: false },
  { key: 'tier', label: 'Account Tier', required: false },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'website', label: 'Website', required: false },
  { key: 'location', label: 'Location', required: false },
  { key: 'notes', label: 'Notes', required: false },
  // Primary contact columns
  { key: '_contact_name', label: 'Contact Name', required: false },
  { key: '_contact_role', label: 'Contact Role', required: false },
  { key: '_contact_email', label: 'Contact Email', required: false },
  { key: '_contact_phone', label: 'Contact Phone', required: false },
];

const CONTACT_FIELDS = [
  { key: 'name', label: 'Contact Name', required: true },
  { key: 'account', label: 'Company Name', required: true },
  { key: 'role', label: 'Role', required: false },
  { key: 'email', label: 'Email', required: false },
  { key: 'phone', label: 'Phone', required: false },
];

// ── Auto-detect column → field mapping ───────────────────────────────────────

const ACCOUNT_SYNONYMS = {
  name: ['company', 'account', 'company name', 'account name', 'name', 'customer'],
  industry: ['industry', 'sector'],
  tier: ['tier', 'account tier', 'level'],
  phone: ['phone', 'tel', 'telephone', 'phone number'],
  website: ['website', 'web', 'url', 'site'],
  location: ['location', 'city', 'address', 'region'],
  notes: ['notes', 'note', 'comments', 'description'],
  _contact_name: ['contact name', 'contact', 'primary contact', 'poc name'],
  _contact_role: ['contact role', 'contact title', 'poc role', 'poc title'],
  _contact_email: ['contact email', 'poc email', 'email'],
  _contact_phone: ['contact phone', 'poc phone'],
};

const CONTACT_SYNONYMS = {
  name: ['contact name', 'name', 'full name', 'contact'],
  account: ['company', 'account', 'company name', 'account name', 'organization'],
  role: ['role', 'title', 'position', 'job title'],
  email: ['email', 'email address', 'e-mail'],
  phone: ['phone', 'tel', 'telephone', 'mobile'],
};

function autoMap(headers, type) {
  const synonyms = type === 'accounts' ? ACCOUNT_SYNONYMS : CONTACT_SYNONYMS;
  const mapping = {};
  headers.forEach((h) => {
    const hl = (h || '').toLowerCase().trim();
    for (const [fieldKey, syns] of Object.entries(synonyms)) {
      if (syns.some((s) => hl === s || hl.includes(s))) {
        if (!Object.values(mapping).includes(fieldKey)) {
          mapping[h] = fieldKey;
          break;
        }
      }
    }
    if (!mapping[h]) mapping[h] = '';
  });
  return mapping;
}

// ── File parsing ─────────────────────────────────────────────────────────────

function parseFile(file) {
  return new Promise((resolve, reject) => {
    if (file.size > 10 * 1024 * 1024) {
      reject(new Error('File is too large. Maximum size is 10 MB.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
        resolve(rows);
      } catch (err) {
        reject(new Error('Could not read file. Make sure it is a valid .xlsx, .xls, or .csv file.'));
      }
    };
    reader.onerror = () => reject(new Error('File could not be read.'));
    reader.readAsArrayBuffer(file);
  });
}

// ── Template download ────────────────────────────────────────────────────────

function downloadTemplate(type) {
  const columns =
    type === 'accounts'
      ? [
          'Company Name',
          'Industry',
          'Account Tier',
          'Phone',
          'Website',
          'Location',
          'Notes',
          'Contact Name',
          'Contact Role',
          'Contact Email',
          'Contact Phone',
        ]
      : ['Contact Name', 'Company Name', 'Role', 'Email', 'Phone'];
  const ws = XLSX.utils.aoa_to_sheet([columns]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Template');
  XLSX.writeFile(wb, `globalops_${type}_template.xlsx`);
}

// ── Step indicator ────────────────────────────────────────────────────────────

function StepIndicator({ step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, justifyContent: 'center', marginBottom: 24 }}>
      {[1, 2, 3].map((n, i) => (
        <div key={n} style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 800,
              fontFamily: 'Montserrat',
              background: step >= n ? '#1B5E20' : '#F0F0F0',
              color: step >= n ? '#fff' : '#aaa',
            }}
          >
            {n}
          </div>
          {i < 2 && <div style={{ width: 48, height: 2, background: step > n ? '#1B5E20' : '#F0F0F0' }} />}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 56, marginTop: 6, position: 'absolute', marginLeft: -4 }}>
        {['Upload', 'Preview', 'Result'].map((label, i) => (
          <span key={label} style={{ fontSize: 9.5, fontWeight: 700, color: step >= i + 1 ? '#1B5E20' : '#bbb', fontFamily: 'Montserrat' }}>
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ImportModal({ isOpen, onClose, type, allAccounts, fetchAll }) {
  const drag = useDraggableDialog(isOpen); // moved by its header (10/9/26)
  const [step, setStep] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [parseError, setParseError] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const fields = type === 'accounts' ? ACCOUNT_FIELDS : CONTACT_FIELDS;
  const title = type === 'accounts' ? 'Import Accounts' : 'Import Contacts';

  const reset = useCallback(() => {
    setStep(1);
    setDragging(false);
    setFile(null);
    setRows([]);
    setHeaders([]);
    setMapping({});
    setParseError('');
    setImporting(false);
    setResult(null);
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  // File selection
  const handleFile = async (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setParseError('Invalid file type. Please upload a .xlsx, .xls, or .csv file.');
      return;
    }
    setParseError('');
    setFile(f);
    try {
      const data = await parseFile(f);
      if (data.length === 0) {
        setParseError('The file is empty or has no data rows.');
        return;
      }
      const hdrs = Object.keys(data[0]);
      setHeaders(hdrs);
      setRows(data);
      setMapping(autoMap(hdrs, type));
      setStep(2);
    } catch (err) {
      setParseError(err.message);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = filesOf(e.dataTransfer)[0];
    if (f) handleFile(f);
  };

  // Mapped value for a row
  const getMappedValue = (row, fieldKey) => {
    const col = Object.entries(mapping).find(([, v]) => v === fieldKey)?.[0];
    return col ? String(row[col] || '').trim() : '';
  };

  // Normalise tier: "1" → "Tier 1", "tier1" → "Tier 1", passthrough if already correct
  const normalizeTier = (raw) => {
    if (!raw) return '';
    const s = String(raw).trim();
    if (/^tier\s*[123]$/i.test(s)) return s.replace(/tier\s*/i, 'Tier ');
    if (/^[123]$/.test(s)) return `Tier ${s}`;
    return s;
  };

  // Validation: check required fields are mapped
  const requiredFields = fields.filter((f) => f.required);
  const missingRequired = requiredFields.filter((f) => !Object.values(mapping).includes(f.key));

  // ── Import logic ────────────────────────────────────────────────────────────

  const runImport = async () => {
    setImporting(true);
    let imported = 0;
    let skipped = 0;
    const errors = [];

    try {
      if (type === 'accounts') {
        for (const row of rows) {
          const name = getMappedValue(row, 'name');
          if (!name) {
            skipped++;
            continue;
          }

          const payload = {
            name,
            industry: getMappedValue(row, 'industry') || undefined,
            tier: normalizeTier(getMappedValue(row, 'tier')) || undefined,
            phone: getMappedValue(row, 'phone') || undefined,
            website: getMappedValue(row, 'website') || undefined,
            location: getMappedValue(row, 'location') || undefined,
            notes: getMappedValue(row, 'notes') || undefined,
            // an imported list is tracked, not a business-development lead (Paul, 10/21/26)
            designation: 'Account',
          };

          try {
            const res = await fetch('/api/accounts', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            if (!res.ok) {
              skipped++;
              errors.push(`Row "${name}": ${res.statusText}`);
              continue;
            }
            const acct = await res.json();
            imported++;

            // Post primary contact if contact name column is mapped
            const contactName = getMappedValue(row, '_contact_name');
            if (contactName && acct.id) {
              await fetch(`/api/accounts/${acct.id}/contacts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  name: contactName,
                  role: getMappedValue(row, '_contact_role') || undefined,
                  email: getMappedValue(row, '_contact_email') || undefined,
                  phone: getMappedValue(row, '_contact_phone') || undefined,
                }),
              });
            }
          } catch (err) {
            skipped++;
            errors.push(`Row "${name}": network error`);
          }
        }

        // Log import activity for each account created (one summary activity on first account)
        if (imported > 0 && allAccounts.length > 0) {
          // Find first account to attach the import log — use first in allAccounts as anchor
          const firstAcct = allAccounts[0];
          await fetch(`/api/accounts/${firstAcct.id}/activity`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              date: new Date().toISOString().slice(0, 10),
              text: `Imported ${imported} account${imported !== 1 ? 's' : ''} from "${file.name}"`,
            }),
          });
        }
      } else {
        // Contacts import
        for (const row of rows) {
          const contactName = getMappedValue(row, 'name');
          const companyName = getMappedValue(row, 'account');
          if (!contactName || !companyName) {
            skipped++;
            continue;
          }

          // Match account by case-insensitive partial name
          const matched = (allAccounts || []).find(
            (a) => a.name.toLowerCase().includes(companyName.toLowerCase()) || companyName.toLowerCase().includes(a.name.toLowerCase())
          );

          if (!matched) {
            skipped++;
            errors.push(`"${contactName}": no account matched "${companyName}"`);
            continue;
          }

          try {
            const res = await fetch(`/api/accounts/${matched.id}/contacts`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: contactName,
                role: getMappedValue(row, 'role') || undefined,
                email: getMappedValue(row, 'email') || undefined,
                phone: getMappedValue(row, 'phone') || undefined,
              }),
            });
            if (!res.ok) {
              skipped++;
              errors.push(`"${contactName}": ${res.statusText}`);
              continue;
            }
            imported++;
          } catch (err) {
            skipped++;
            errors.push(`"${contactName}": network error`);
          }
        }
      }

      await fetchAll();
      setResult({ imported, skipped, errors });
      setStep(3);
    } catch (err) {
      setResult({ imported, skipped, errors: [...errors, `Unexpected error: ${err.message}`] });
      setStep(3);
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  // ── Step 1: Upload ──────────────────────────────────────────────────────────
  const renderStep1 = () => (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div
          style={dragging ? DROP_ZONE_DRAG : DROP_ZONE_BASE}
          onDragEnter={() => setDragging(true)}
          onDragLeave={() => setDragging(false)}
          onDragOver={acceptDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1B5E20', marginBottom: 4, fontFamily: 'Montserrat' }}>
            Drop your file here
          </div>
          <div style={{ fontSize: 11, color: '#888', fontFamily: 'Montserrat' }}>Accepts .xlsx, .xls, .csv — max 10 MB</div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files[0])}
          />
        </div>
        {parseError && (
          <div
            style={{
              marginTop: 12,
              padding: '10px 14px',
              background: '#FFEBEE',
              borderRadius: 6,
              fontSize: 11,
              color: '#B71C1C',
              fontFamily: 'Montserrat',
            }}
          >
            {parseError}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderTop: '1px solid #F5F5F5' }}>
        <div style={{ fontSize: 11, color: '#888', fontFamily: 'Montserrat' }}>Not sure about the format?</div>
        <button
          onClick={() => downloadTemplate(type)}
          style={{ ...BTN_OUTLINE, padding: '7px 14px', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Icon name="download" size={11} color="#1B5E20" /> Download Template
        </button>
      </div>
    </div>
  );

  // ── Step 2: Preview & Map ───────────────────────────────────────────────────
  const renderStep2 = () => {
    const previewRows = rows.slice(0, 5);
    return (
      <div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: '#555', fontFamily: 'Montserrat', marginBottom: 12 }}>
            <strong>{rows.length}</strong> rows detected in <strong>{file?.name}</strong>. Map your columns to GlobalOps fields below.
          </div>

          {/* Column mapping */}
          <div style={{ background: '#FAFBFC', borderRadius: 8, padding: 16, border: '1px solid #ECECEC', marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
              Column Mapping
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '8px 12px', alignItems: 'center' }}>
              {headers.map((h) => (
                <div key={h} style={{ display: 'contents' }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#333',
                      fontFamily: 'Montserrat',
                      padding: '6px 10px',
                      background: '#fff',
                      borderRadius: 4,
                      border: '1px solid #E8E8E8',
                    }}
                  >
                    {h}
                  </div>
                  <div style={{ textAlign: 'center', color: '#bbb', fontSize: 14 }}>→</div>
                  <div>
                    <select
                      style={SELECT}
                      value={mapping[h] || ''}
                      onChange={(e) => setMapping((prev) => ({ ...prev, [h]: e.target.value }))}
                    >
                      <option value="">(ignore)</option>
                      {fields.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}
                          {f.required ? ' *' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Missing required fields warning */}
          {missingRequired.length > 0 && (
            <div
              style={{
                padding: '10px 14px',
                background: '#FFF8E1',
                borderRadius: 6,
                fontSize: 11,
                color: '#E65100',
                fontFamily: 'Montserrat',
                marginBottom: 12,
              }}
            >
              Required fields not mapped: <strong>{missingRequired.map((f) => f.label).join(', ')}</strong>
            </div>
          )}

          {/* Preview table */}
          <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
            Preview (first {previewRows.length} rows)
          </div>
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #ECECEC' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: 'Montserrat' }}>
              <thead>
                <tr>
                  {headers.map((h) => (
                    <th key={h} style={TABLE_TH}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i}>
                    {headers.map((h) => (
                      <td key={h} style={TABLE_TD}>
                        {String(row[h] ?? '').slice(0, 60)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 5 && (
            <div style={{ fontSize: 10.5, color: '#aaa', marginTop: 6, fontFamily: 'Montserrat' }}>
              …and {rows.length - 5} more row{rows.length - 5 !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Step 3: Result ──────────────────────────────────────────────────────────
  const renderStep3 = () => (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        {result.imported > 0 ? (
          <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
        ) : (
          <div style={{ fontSize: 40, marginBottom: 8 }}>⚠️</div>
        )}
        <div style={{ fontSize: 15, fontWeight: 800, color: '#0D0D0D', fontFamily: 'Montserrat', marginBottom: 4 }}>Import Complete</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <div style={{ background: '#E8F5E9', borderRadius: 10, padding: '16px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#1B5E20' }}>{result.imported}</div>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: '#1B5E20', opacity: 0.8, fontFamily: 'Montserrat' }}>Imported</div>
        </div>
        <div style={{ background: '#FFF8E1', borderRadius: 10, padding: '16px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#E65100' }}>{result.skipped}</div>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: '#E65100', opacity: 0.8, fontFamily: 'Montserrat' }}>Skipped</div>
        </div>
      </div>

      {result.errors.length > 0 && (
        <div style={{ background: '#FFEBEE', borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: '#B71C1C', marginBottom: 6, fontFamily: 'Montserrat' }}>
            Skipped rows ({result.errors.length}):
          </div>
          <div style={{ maxHeight: 120, overflowY: 'auto' }}>
            {result.errors.map((e, i) => (
              <div key={i} style={{ fontSize: 10.5, color: '#B71C1C', fontFamily: 'Montserrat', padding: '2px 0' }}>
                • {e}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    // closes only when the press started on the backdrop: a drag out of a field must not close it
    <div
      style={MODAL_OVERLAY}
      onMouseDown={(e) => {
        e.currentTarget.dataset.pressed = e.target === e.currentTarget ? '1' : '';
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && e.currentTarget.dataset.pressed === '1') handleClose();
      }}
    >
      <div style={{ ...MODAL_BOX, ...drag.style }} className="go-pop-in" ref={drag.ref} onPointerDown={drag.onPointerDown}>
        {/* Header */}
        <div data-dialog-handle="" style={HEADER}>
          <span style={{ fontSize: 14, fontWeight: 800, color: '#0D0D0D', fontFamily: 'Montserrat' }}>{title}</span>
          <div onClick={handleClose} style={{ cursor: 'pointer', color: '#aaa', padding: 4 }}>
            <Icon name="x" size={16} color="#aaa" />
          </div>
        </div>

        {/* Body */}
        <div style={BODY}>
          <div style={{ position: 'relative', paddingTop: 8 }}>
            <StepIndicator step={step} />
          </div>
          <div style={{ marginTop: 24 }}>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </div>
        </div>

        {/* Footer */}
        <div style={FOOTER}>
          {step === 1 && (
            <button onClick={handleClose} style={BTN_CANCEL}>
              Cancel
            </button>
          )}
          {step === 2 && (
            <>
              <button
                onClick={() => {
                  setStep(1);
                  setFile(null);
                  setRows([]);
                  setParseError('');
                }}
                style={BTN_CANCEL}
              >
                Back
              </button>
              <button
                onClick={runImport}
                disabled={importing || missingRequired.length > 0}
                style={{
                  ...BTN_PRIMARY,
                  opacity: importing || missingRequired.length > 0 ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {importing ? 'Importing…' : `Import ${rows.length} Row${rows.length !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
          {step === 3 && (
            <>
              {result?.imported > 0 && (
                <button
                  onClick={() => {
                    reset();
                  }}
                  style={BTN_OUTLINE}
                >
                  Import Another
                </button>
              )}
              <button onClick={handleClose} style={BTN_PRIMARY}>
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
